'use client';

import { Popover } from '@base-ui/react/popover';
import { css } from '@repo/pandacss/styled-system/css';
import { DisclosureTooltip } from '@repo/ui-v2/DisclosureTooltip';
import type { CSSProperties } from 'react';
import { useMemo } from 'react';
import { MdHelpOutline } from 'react-icons/md';
import { numericLayoutTooltipClassName } from '../disclosureTooltipPopover';
import { StarIcon, type StarIconSize } from './components/StarIcon';

export type { StarIconSize };

const tooltipHelpIconClass = css({
  fontSize: '[1em]',
  color: '[var(--numeric-layout-tooltip-icon-color)]',
});

const tooltipBodyClass = css({
  textStyle: 'textSmall',
  color: '[var(--numeric-layout-tooltip-color)]',
});

type Props = {
  starRating?: number;
  starRatingTooltip?: string;
  reviewLink?: string;
  hideLabel?: boolean;
  tooltipColor?: string;
  iconSize?: StarIconSize;
  tooltipIconColor?: string;
};

export const NumericLayout = ({
  starRating,
  starRatingTooltip,
  reviewLink,
  hideLabel = false,
  tooltipColor = 'black',
  iconSize = 24,
  tooltipIconColor = 'black',
}: Props) => {
  const handler = useMemo(() => Popover.createHandle<unknown>(), []);

  if (!starRating)
    return (
      <span
        className={css({
          display: 'block',
          color: 'neutral.dark',
          textStyle: 'textSmall',
        })}
      >
        <StarIcon
          color="#D8D9DA"
          iconSize={iconSize}
          className={css({ verticalAlign: 'bottom' })}
        />{' '}
        Not yet rated
      </span>
    );

  return (
    <div className={css({ display: 'flex', flexDirection: 'column' })}>
      <div className={css({ display: 'inline-flex', alignItems: 'center' })}>
        {!hideLabel && (
          <span
            className={css({
              textStyle: 'textSmall',
              color: 'neutral.dark',
              paddingRight: '1',
            })}
          >
            NerdWallet rating
          </span>
        )}
        {starRatingTooltip && (
          <>
            <Popover.Trigger
              handle={handler}
              className={css({
                borderTopStyle: 'none',
                borderRightStyle: 'none',
                borderBottomStyle: 'none',
                borderLeftStyle: 'none',
                backgroundColor: 'transparent',
                paddingTop: '0',
                paddingRight: '0',
                paddingBottom: '0',
                paddingLeft: '0',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                color: 'primary',
                textStyle: 'textSmall',
                lineHeight: 'xs',
                verticalAlign: 'middle',
              })}
              aria-label="NerdWallet rating info"
            >
              <MdHelpOutline
                className={tooltipHelpIconClass}
                style={
                  {
                    '--numeric-layout-tooltip-icon-color': tooltipIconColor,
                  } as CSSProperties
                }
              />
            </Popover.Trigger>
            <DisclosureTooltip handler={handler} tooltipClassName={numericLayoutTooltipClassName}>
              <span
                className={tooltipBodyClass}
                style={
                  {
                    '--numeric-layout-tooltip-color': tooltipColor,
                  } as CSSProperties
                }
              >
                {starRatingTooltip}
              </span>
            </DisclosureTooltip>
          </>
        )}
      </div>
      <div
        className={css({
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          fontSize: 'xxl',
          marginRight: '1',
        })}
      >
        <StarIcon iconSize={iconSize} className={css({ marginRight: '[5px]' })} />
        {reviewLink ? (
          <a
            href={reviewLink}
            target="_blank"
            rel="noopener noreferrer"
            className={css({
              textStyle: { base: 'bodyLarge', md: 'bodyExtraLarge' },
              color: 'blue.main',
              textDecorationLine: 'none',
              _hover: {
                textDecorationLine: 'underline',
              },
            })}
          >
            {starRating?.toFixed(1)}
          </a>
        ) : (
          starRating?.toFixed(1)
        )}
        <span
          className={css({
            textStyle: 'textSmall',
            color: 'neutral.dark',
            paddingTop: '1',
            paddingLeft: '1',
          })}
        >
          /5
        </span>
      </div>
    </div>
  );
};
